import React, { useEffect, useState } from "react";
import { useQuoteContext } from "../../../contexts/QuoteContext/QuoteContext";
import { useQuoteDetailsContext } from "../../../contexts/QuoteDetailsContext";
import ErrorComponentWrapper from "../../ErrorHandling/ErrorComponentWrapper";
import Payment from "./Payment";
import { ErrorCode, ErrorDetails, PaymentInfoStatus, QuotePaymentStatus } from "../../../Common/Types";
import { getQuotePaymentStatus } from "../../../Common/services/PaymentsService";
import { getPaidQuote } from "../../../Common/services/QuoteService";
import { QuoteCardWithTitle } from "../../QuoteList/QuoteCard/QuoteCardWithTitle";
import { LabelKey } from "../../../Common/StoryblokTypes";
import { useDatasources } from "../../../contexts/StoryblokContext/StoryblokContext";

const PaymentPage: React.FC = () => {
  const { selected, isQuoteListLoading, setupError, quotes } = useQuoteContext();
  const {
    loadingQuote,
    loadingQuoteError,
    reservationPrice,
    loadingInstallments,
    installments,
    paidFullDiscount,
    fetchInstallments,
  } = useQuoteDetailsContext();
  const { labels } = useDatasources();
  const [paymentPageError, setPaymentPageError] = useState<ErrorDetails | undefined>(undefined);
  const paidOrReservedQuote = getPaidQuote(quotes);

  useEffect(() => {
    const paymentStatus = getQuotePaymentStatus(selected?.quoteData);
    if (paymentStatus === PaymentInfoStatus.Paid) {
      setPaymentPageError({
        code: ErrorCode.TryingToPayAlreadyPaidQuote,
        details: {
          fallbackMessage: "Quote has been already paid.",
        },
      });
    } else if (selected?.quoteData.status === QuotePaymentStatus.Canceled) {
      setPaymentPageError({
        code: ErrorCode.QuoteIsCancelled,
        hideAdditionalDetails: true,
        details: {
          fallbackMessage: "Since another quote has been paid or reserved, this quote has been cancelled.",
        },
      });
    } else {
      setPaymentPageError(undefined);
    }
  }, [selected]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!selected?.quoteData.id) {
      return;
    }
    fetchInstallments();
  }, [selected?.quoteData.id, fetchInstallments]);

  return (
    <ErrorComponentWrapper
      errorFooter={
        paymentPageError?.code === ErrorCode.QuoteIsCancelled && (
          <QuoteCardWithTitle quote={paidOrReservedQuote} title={labels[LabelKey.suggestedReservedQuote]} />
        )
      }
      isLoading={
        loadingInstallments ||
        installments === undefined ||
        paidFullDiscount === undefined ||
        loadingQuote ||
        isQuoteListLoading ||
        !selected ||
        !reservationPrice
      }
      error={loadingQuoteError || setupError || paymentPageError}
    >
      {selected && reservationPrice && <Payment quote={selected.quoteData} reservationPrice={reservationPrice} />}
    </ErrorComponentWrapper>
  );
};

export default PaymentPage;
